import React, { useEffect, useLayoutEffect, useRef } from "react";
import PropTypes from "prop-types";
import MessageBubble from "./MessageBubble";
import { STATUS_META, PRIORITY_META, formatDate } from "../supportConstants";
import styles from "./TicketDetail.module.css";

const REPLY_MAX_HEIGHT = 120; // ~4 linhas

/**
 * Painel direito: header + thread (descrição como 1ª bolha) + campo de resposta
 * (ou banner de encerrado). Scroll automático para a última mensagem ao abrir o
 * detalhe e ao chegar nova mensagem (incl. otimista).
 *
 * Toda a lógica de rede vive no container (Support.js); aqui é apresentação +
 * estado local do textarea.
 */
export default function TicketDetail({
  ticket,
  loading,
  reply,
  onReplyChange,
  onSend,
  sending,
  onClose,
  onBack,
  isMobile,
}) {
  const threadEndRef = useRef(null);
  const textareaRef = useRef(null);

  const status = ticket ? STATUS_META[ticket.status] ?? STATUS_META.OPEN : null;
  const priority = ticket ? PRIORITY_META[ticket.priority] : null;

  // RESOLVED e CLOSED são "encerrados" para o usuário → sem campo de resposta.
  const isClosedLike = ticket && (ticket.status === "CLOSED" || ticket.status === "RESOLVED");
  const canClose = ticket && ticket.status !== "CLOSED" && ticket.status !== "RESOLVED";

  const messageCount = ticket?.messages?.length ?? 0;

  // Scroll para a última mensagem ao abrir o detalhe e a cada nova mensagem.
  useLayoutEffect(() => {
    threadEndRef.current?.scrollIntoView({ block: "end" });
  }, [ticket?.id, messageCount]);

  // Auto-grow do textarea (até REPLY_MAX_HEIGHT).
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, REPLY_MAX_HEIGHT)}px`;
  }, [reply]);

  const handleTextareaKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (reply.trim() && !sending) onSend();
    }
  };

  if (!ticket && loading) {
    return (
      <div className={styles.detail}>
        <DetailSkeleton />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className={styles.detail}>
        <div className={styles.placeholder}>
          <span className={styles.placeholderIcon} aria-hidden="true">💬</span>
          <p className={styles.placeholderText}>
            Selecione um chamado à esquerda ou crie um novo.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.detail}>
      <div className={styles.header}>
        <div className={styles.headerMain}>
          {isMobile && (
            <button type="button" className={styles.backBtn} onClick={onBack}>
              ← Meus chamados
            </button>
          )}
          <h2 className={styles.subject}>{ticket.subject}</h2>
          <div className={styles.metaRow}>
            <span className={styles.opened}>Aberto em {formatDate(ticket.createdAt)}</span>
            <span
              className={styles.statusBadge}
              style={{ background: status.soft, color: status.color }}
            >
              <span aria-hidden="true">{status.emoji}</span> {status.label}
            </span>
            {priority?.show && (
              <span
                className={styles.priorityBadge}
                style={{ background: priority.soft, color: priority.color }}
              >
                {priority.label}
              </span>
            )}
          </div>
        </div>
        {canClose && (
          <button type="button" className={styles.closeBtn} onClick={onClose}>
            Encerrar chamado
          </button>
        )}
      </div>

      <div className={styles.thread}>
        {ticket.description && (
          <MessageBubble
            content={ticket.description}
            isStaff={false}
            createdAt={ticket.createdAt}
            isDescription
          />
        )}
        {(ticket.messages ?? []).map((msg) => (
          <MessageBubble
            key={msg.id ?? msg._tempId}
            content={msg.content}
            isStaff={!!msg.isStaff}
            createdAt={msg.createdAt}
            pending={!!msg._pending}
          />
        ))}
        <div ref={threadEndRef} />
      </div>

      {isClosedLike ? (
        <div className={styles.closedBanner} role="status">
          🔒 Este chamado está encerrado. Abra um novo chamado se precisar de mais ajuda.
        </div>
      ) : (
        <div className={styles.replyBar}>
          <textarea
            ref={textareaRef}
            value={reply}
            onChange={(e) => onReplyChange(e.target.value)}
            onKeyDown={handleTextareaKeyDown}
            className={styles.replyInput}
            placeholder="Digite sua mensagem…"
            rows={1}
            aria-label="Digite sua mensagem para o suporte"
          />
          <button
            type="button"
            className={styles.sendBtn}
            onClick={onSend}
            disabled={sending || !reply.trim()}
          >
            {sending ? "Enviando…" : "Enviar"}
          </button>
        </div>
      )}
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className={styles.skeletonWrap} aria-hidden="true">
      <div className={`${styles.skeleton} ${styles.skHeader}`} />
      <div className={styles.skThread}>
        <div className={`${styles.skeleton} ${styles.skBubbleRight}`} />
        <div className={`${styles.skeleton} ${styles.skBubbleLeft}`} />
        <div className={`${styles.skeleton} ${styles.skBubbleRight}`} />
      </div>
    </div>
  );
}

TicketDetail.propTypes = {
  ticket: PropTypes.object,
  loading: PropTypes.bool,
  reply: PropTypes.string.isRequired,
  onReplyChange: PropTypes.func.isRequired,
  onSend: PropTypes.func.isRequired,
  sending: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  isMobile: PropTypes.bool,
};
